import React, { useState, useMemo, useEffect, useRef } from "react";
import classNames from "classnames";
import { Spinner } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "utils/colorUtils";
import { useGetChannels } from "api/channels/service";
import { formatPriceWithDollar } from "utils/currency";
import { formatAccountListStatusLabel, getAccountStatusColor } from "utils/accountStatusColors";
import { useGetCalculuses } from "api/calculuses/service";
import AccountsFilters from "views/accounts/components/AccountsFilters";
import {
    getTheme2MultiSelectCss,
    getTheme2SearchFilterFieldCss,
    getTheme2SearchFilterStatusFieldCss,
    THEME2_SEARCH_FILTER_ACCOUNTS_WIDE_FIELD_WIDTH,
} from "utils/theme2MultiSelect";

const SEARCH_ACCOUNTS_STATUS_PREFIX = "search-accounts-status";
const SEARCH_ACCOUNTS_LOCATION_PREFIX = "search-accounts-location";

const SA_ACCOUNTS_TAB_STYLES = (isTheme2) => `
  ${isTheme2 ? `
  ${getTheme2SearchFilterFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-accounts-tab",
        filtersSelector: ".sa-accounts-filters",
        rowClass: "search-accounts-filters-row",
        colClass: "search-accounts-filter-col",
    })}
  ${getTheme2SearchFilterStatusFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2",
        filterColClass: "search-accounts-filter-col",
        wideModifiers: ["status", "location"],
        fieldWidth: THEME2_SEARCH_FILTER_ACCOUNTS_WIDE_FIELD_WIDTH,
    })}
  .tenant-edit-page.tenant-edit-page--theme2 .sa-accounts-tab .search-accounts-filters-row {
    column-gap: 4px;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .sa-accounts-tab .search-accounts-filter-col {
    padding-left: 4px;
    padding-right: 4px;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .sa-accounts-tab .search-accounts-filter-col:first-child {
    padding-left: 0;
  }
  ${getTheme2MultiSelectCss({
        prefix: SEARCH_ACCOUNTS_STATUS_PREFIX,
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-accounts-tab",
    })}
  ${getTheme2MultiSelectCss({
        prefix: SEARCH_ACCOUNTS_LOCATION_PREFIX,
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-accounts-tab",
    })}
  ` : ""}
`;

const getStatusOptions = (t) => [
    { value: "active", label: t('accountStatus.active') },
    { value: "bankruptcy", label: t('accountStatus.bankruptcy') },
    { value: "closed", label: t('accountStatus.closed') },
    { value: "deleted", label: t('accountStatus.deleted') },
    { value: "inactive", label: t('accountStatus.inactive') },
    { value: "out_for_repo", label: t('accountStatus.outForRepo') },
    { value: "repossessed", label: t('accountStatus.repossessed') },
    { value: "skip", label: t('accountStatus.skip') },
    { value: "transferred", label: t('accountStatus.transferred') },
    { value: "charge_off", label: t('accountStatus.chargeOff') },
];

const getAccountTypeOptions = (t) => [
    { value: "local_lender_finance", label: t('accounts.localLenderFinance') },
    { value: "in_house_finance", label: t('accounts.inHouseFinance') },
    { value: "cash", label: t('accounts.cash') },
    { value: "wholesale", label: t('accounts.wholesale') },
];

const SAAccountsTab = ({ activeColor = "blue", isTheme2 = false }) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const navigate = useNavigate();

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState([]);
    const [locations, setLocations] = useState([]);
    const [accountType, setAccountType] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const statusArray = useMemo(() => status.map((option) => option.value), [status]);
    const locationsArray = useMemo(() => locations.map((option) => option.value), [locations]);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(handler);
    }, [search]);

    const AccountsTable = isTheme2 ? ReactTable2 : ReactTable;
    const { data: calculuses, isLoading } = useGetCalculuses(
        debouncedSearch,
        statusArray,
        page,
        pageSize,
        locationsArray,
        accountType
    );

    const statusOptionsData = useMemo(() => getStatusOptions(t), [t]);
    const accountTypeOptionsData = useMemo(() => getAccountTypeOptions(t), [t]);
    const { data: locationsData } = useGetChannels("sales_location_branch");

    const initializedRef = useRef(false);
    useEffect(() => {
        if (!initializedRef.current && statusOptionsData.length > 0) {
            const defaults = ["active", "repossessed", "out_for_repo"];
            const defaultOptions = statusOptionsData.filter((opt) => defaults.includes(opt.value));
            setStatus(defaultOptions);
            initializedRef.current = true;
        }
    }, [statusOptionsData]);

    const accountColumns = [
        {
            Header: t('accounts.borrowerName'),
            accessor: "borrower_name",
            width: 150,
            Cell: ({ value }) => (
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", whiteSpace: "normal" }}>
                    {value}
                </div>
            )
        },
        {
            accessor: "information",
            Header: <div style={{ textAlign: 'center', lineHeight: '1.3' }}>{t('accounts.acct')}<br />{t('accounts.loan')}<br />{t('accounts.homePhone')}<br />{t('accounts.cellPhone')}</div>,
            Cell: ({ value }) => (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", textAlign: 'center' }}>
                    <span>{value?.booking_id || ""}</span>
                    <span>{value?.loan || ""}</span>
                    <span>{value?.home_phone || ""}</span>
                    <span>{value?.cell_phone || ""}</span>
                </div>
            )
        },
        {
            Header: <div style={{ textAlign: 'center' }}>{t('accounts.stkNumber')}</div>,
            accessor: "stk_number",
            Cell: ({ row }) => (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", textAlign: 'center' }}>
                    <span>{row.original.stk_number}</span>
                    <span style={{ fontSize: "0.85em", opacity: 0.7 }}>{row.original.vin}</span>
                </div>
            )
        },
        {
            Header: <div style={{ textAlign: 'center' }}>{t('accounts.acctStatus')}</div>,
            accessor: "acct_status",
            Cell: ({ row }) => {
                const value = row.original.acct_status;
                const flags = row.original.flags || [];
                const isBankruptcy = Boolean(row.original.is_bankruptcy);
                const displayValue = formatAccountListStatusLabel(value, { flags, isBankruptcy });
                const color = getAccountStatusColor(value, { flags, isBankruptcy });

                return (
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", textAlign: 'center' }}>
                        <span style={{ color: color, fontWeight: 'bold' }}>{displayValue}</span>
                    </div>
                );
            }
        },
        {
            Header: <div style={{ textAlign: 'center' }}>{t('accounts.daysPd', "Days PD")}</div>,
            accessor: "days_pd",
            Cell: ({ value }) => {
                const lateDay = Number(value) || 0;
                let rowBgColor = '#28a745';
                if (lateDay > 0) rowBgColor = '#f6ad55';
                if (lateDay > 15) rowBgColor = '#f6a04d';
                if (lateDay > 30) rowBgColor = '#d97706';
                if (lateDay > 60) rowBgColor = '#bf6d00';
                if (lateDay >= 90) rowBgColor = '#dc3545';

                return (
                    <div style={{ textAlign: "center" }}>
                        <span style={{ backgroundColor: rowBgColor, color: "#fff", padding: "4px 8px", fontSize: "12px", fontWeight: "bold", display: "inline-block", minWidth: "60px" }}>
                            {value}
                        </span>
                    </div>
                );
            },
            width: 100,
        },
        {
            Header: <div style={{ textAlign: "right" }}>{t("accounts.totalBalance")}</div>,
            accessor: "total_balance",
            Cell: ({ value }) => <div style={{ textAlign: "right" }}>{value}</div>,
        },
    ];

    const rows = useMemo(() => {
        return (calculuses?.results || []).map((item) => ({
            borrower_name: item.customer?.full_name || "",
            documentId: item.documentId || "",
            booking_id: item.booking_id,
            days_pd: item.days_pd,
            information: {
                booking_id: item.booking_id || "",
                loan: item.inventory?.stock_number || "",
                home_phone: item.customer?.home_phone || "",
                cell_phone: item.customer?.cell_phone || "",
            },
            stk_number: item.inventory?.stock_number || "",
            vin: item.inventory?.vin || "",
            acct_status: item.act_status || "",
            flags: item.flags || [],
            is_bankruptcy: Boolean(item.is_bankruptcy),
            total_balance: item.total_balance ? formatPriceWithDollar(item.total_balance) : "",
        }));
    }, [calculuses]);

    return (
        <div
            className={classNames("sa-accounts-tab", {
                "sa-accounts-tab--theme2": isTheme2,
            })}
        >
            <style>{SA_ACCOUNTS_TAB_STYLES(isTheme2)}</style>
            <div className="sa-accounts-filters mb-3">
                <AccountsFilters
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                    search={search}
                    onSearchChange={setSearch}
                    status={status}
                    onStatusChange={setStatus}
                    accountType={accountType}
                    onAccountTypeChange={setAccountType}
                    locations={locations}
                    onLocationsChange={setLocations}
                    statusOptions={statusOptionsData}
                    accountTypeOptions={accountTypeOptionsData}
                    locationsData={locationsData}
                />
            </div>

            <div
                className={isTheme2 ? "inventory-table-wrap" : undefined}
                style={isTheme2 ? { fontSize: "13px", minWidth: 0, maxWidth: "100%" } : undefined}
            >
                {isTheme2 ? (
                    <AccountsTable
                        activeColor={activeColor}
                        data={rows}
                        columns={accountColumns}
                        disableViewportClamp
                        className="-striped -highlight sticky-stock-table"
                        manualPagination={true}
                        pageCount={calculuses?.pagination?.pageCount || 1}
                        pageIndex={page - 1}
                        pageSize={pageSize}
                        totalItems={calculuses?.pagination?.total}
                        loading={isLoading}
                        loadingText={t("accounts.loading")}
                        noDataText={t("accounts.noRecordsFound")}
                        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        onRowClick={(rowData) => {
                            if (rowData.booking_id) {
                                navigate(`/accounts/${rowData.booking_id}`);
                            }
                        }}
                    />
                ) : isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        <Spinner color={activeColorStyle.borderColor} />
                    </div>
                ) : (
                    <AccountsTable
                        activeColor={activeColor}
                        data={rows}
                        columns={accountColumns}
                        className="-striped -highlight"
                        manualPagination={true}
                        pageCount={calculuses?.pagination?.pageCount || 1}
                        pageIndex={page - 1}
                        pageSize={pageSize}
                        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        onRowClick={(rowData) => {
                            if (rowData.booking_id) {
                                navigate(`/accounts/${rowData.booking_id}`);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default SAAccountsTab;
